import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { FaArrowRight, FaUsersCog } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../api";

export default function SchoolAdmins() {
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  // Fetch Schools
  const fetchSchools = async () => {
    try {
      const res = await api.get("/super_admin/school/list");
      setSchools(res.data?.data || []);
    } catch (err) {
      console.error("Fetch schools error:", err);
      setSchools([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  return (
    <Paper
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow: "0 16px 40px rgba(16,42,67,0.1)",
        border: "1px solid rgba(78,102,135,0.14)",
        background: "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(239,250,250,0.92))",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box sx={{ width: 44, height: 44, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 1.5, color: "#fff", background: "linear-gradient(135deg, #2563eb, #0891b2)", boxShadow: "0 8px 18px rgba(37,99,235,0.2)" }}>
            <FaUsersCog size={23} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={900} sx={{ color: "#102a43" }}>
              Admins & Staff
            </Typography>
            <Typography variant="body2" sx={{ color: "#607086", mt: 0.35, fontWeight: 600 }}>
              Select a school to manage its administrators
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ color: "#607086", fontWeight: 700, fontSize: 13 }}>
          {schools.length} {schools.length === 1 ? "school" : "schools"}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={30} sx={{ color: "#0891b2" }} />
        </Box>
      ) : schools.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {schools.map((school, index) => (
            <Paper
              key={school.id}
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.1,
                borderRadius: 2,
                borderColor: "#e2e8f0",
                background: "rgba(255,255,255,0.82)",
                transition: "transform .18s ease, border-color .18s ease, box-shadow .18s ease",
                "&:hover": { transform: "translateX(3px)", borderColor: "rgba(37,99,235,0.45)", boxShadow: "0 8px 20px rgba(16,42,67,0.08)" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                <Box sx={{ width: 32, height: 32, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 1, color: "#2563eb", background: "#e8f1ff", fontSize: 13, fontWeight: 900 }}>
                  {String(index + 1).padStart(2, "0")}
                </Box>
                <Typography sx={{ fontWeight: 800, minWidth: 0, overflowWrap: "anywhere", color: "#172033" }}>
                  {school.name}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                endIcon={<FaArrowRight />}
                onClick={() => navigate(`/super_admin/schooladmins/${school.id}`)}
                sx={{ flexShrink: 0, borderRadius: 1.5, textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg, #2563eb, #0891b2)", boxShadow: "none" }}
              >
                View
              </Button>
            </Paper>
          ))}
        </Box>
      ) : (
        <Typography color="text.secondary" sx={{ py: 2 }}>No schools found.</Typography>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
